/**
 * Lenient double-click detection for canvas interactions.
 *
 * Native browser `dblclick` uses a tight OS distance threshold. With high macOS
 * mouse tracking speed, a few millimeters of hand movement between clicks often
 * exceeds that threshold, so `dblclick` never fires — while single `click` still
 * works. This detector keeps a familiar ~500ms window but allows more screen
 * movement between the two presses.
 */

export const DOUBLE_CLICK_MAX_INTERVAL_MS = 500;

/** Generous so high tracking-speed mice still register a double-click. */
export const DOUBLE_CLICK_MAX_DISTANCE_PX = 28;

export type DoubleClickDetectorOptions = {
  maxIntervalMs?: number;
  maxDistancePx?: number;
};

type DoubleClickPoint = {
  x: number;
  y: number;
  time: number;
  key?: string;
};

export type DoubleClickDetector = {
  /**
   * Record a click. Returns true when this click completes a double-click
   * (same optional key, within time + distance). Consumes the pair so a
   * triple-click does not immediately fire another double.
   */
  record: (x: number, y: number, key?: string) => boolean;
  reset: () => void;
};

const now = (): number =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

export const createDoubleClickDetector = (
  options: DoubleClickDetectorOptions = {},
): DoubleClickDetector => {
  const maxIntervalMs = options.maxIntervalMs ?? DOUBLE_CLICK_MAX_INTERVAL_MS;
  const maxDistancePx = options.maxDistancePx ?? DOUBLE_CLICK_MAX_DISTANCE_PX;
  const maxDistanceSq = maxDistancePx * maxDistancePx;

  let last: DoubleClickPoint | null = null;

  return {
    record(x: number, y: number, key?: string): boolean {
      const time = now();
      const previous = last;
      last = { x, y, time, key };

      if (!previous) {
        return false;
      }

      if (key !== undefined || previous.key !== undefined) {
        if (previous.key !== key) {
          return false;
        }
      }

      if (time - previous.time > maxIntervalMs) {
        return false;
      }

      const dx = x - previous.x;
      const dy = y - previous.y;
      if (dx * dx + dy * dy > maxDistanceSq) {
        return false;
      }

      // Consume the pair so click 3 starts a fresh sequence.
      last = null;
      return true;
    },
    reset() {
      last = null;
    },
  };
};
